import { useCallback, useEffect, useRef, useState } from 'react';
import { listEvents } from '@lib/care-api';
import { useClient } from '@lib/clients';
import { useFreshness, liveStamp } from '@lib/freshness';
import { cachedAnswer, queueCacheKey, readFailed, readStarted, readSucceeded } from '@lib/queue-cache';
import type { Direction, EventSource, EventStatus, ListResp } from '@lib/care-model';

/**
 * useEventQueue - load + live-refresh an event list for a queue. Drives the shared freshness banner,
 * polls every `pollMs` (default 20s), and refetches when the tab regains focus, so multiple operators
 * see a near-live shared queue. `reload()` is for after-mutation refreshes and returns a promise so
 * callers can await a settled refresh.
 *
 * The search term is debounced (300ms) so typing does not refetch per keystroke, and every load is
 * latest-wins (a per-call sequence id) so an out-of-order or slow poll/focus response cannot clobber a
 * newer result.
 */
export interface UseEventQueueOpts {
  direction?: Direction | undefined;
  status?: EventStatus | undefined;
  q?: string;
  /** Include only this origin (Calling page: 'out-calling'). Filtered server-side. */
  source?: EventSource | undefined;
  /** Exclude these origins (member queue: 'out-calling', and 'bad-number' outside its chip). Filtered server-side. */
  notSource?: EventSource | readonly EventSource[] | undefined;
  /**
   * Drop Print Station fax-failure referrals (Inbound queue default). Server-side, because the
   * excluded rows must not reach the totals or the thread rollups either; `faxFailures` on the
   * response still reports how many are being held back.
   */
  excludeFaxFailures?: boolean | undefined;
  /**
   * Also return `threads` + `threadTotals` (Inbound queue, thread view). This ADDS to the body; the
   * `items` array is identical either way, so the Inbound page can flip between the thread and flat
   * views with no refetch.
   */
  groupBy?: 'thread' | undefined;
  pollMs?: number;
  /** Skip loading (e.g. when the page is showing a different sub-view). */
  paused?: boolean;
}

export interface UseEventQueueResult {
  data: ListResp | null;
  loading: boolean;
  reload: () => Promise<void>;
}

/**
 * The session-scoped last-response cache (rev7: page mounts and tab switches render the cached list
 * INSTANTLY and refresh quietly) lives in lib/queue-cache.ts since row 866, so the left column's
 * Inbound queue count reads the same answers instead of asking the server again. Every read below
 * reports how it ended (readStarted, then readSucceeded or readFailed).
 */
export function useEventQueue(opts: UseEventQueueOpts): UseEventQueueResult {
  const {
    direction, status, q = '', source, notSource, excludeFaxFailures, groupBy,
    pollMs = 20_000, paused = false,
  } = opts;
  const { set } = useFreshness();
  const firstLoad = useRef(true);
  const reqSeq = useRef(0);

  // Debounce the typed search so we issue one request after typing settles, not one per keystroke.
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Every field of the query shape is in the key (queueCacheKey: the Calling page and the member queue
  // differ ONLY by source / notSource, and a shared key would let each serve the other's rows), and
  // the selected client leads it: switching clients must never serve the other's rows.
  const client = useClient();
  const cacheKey = queueCacheKey(client, {
    direction, status, source, notSource, excludeFaxFailures, groupBy, q: debouncedQ,
  });
  const [data, setData] = useState<ListResp | null>(() => cachedAnswer(cacheKey) ?? null);
  const [loading, setLoading] = useState(false);

  // Key change (tab switch / new search): serve the cached shape immediately while the fetch runs.
  // On a MISS the previous shape's rows must not masquerade as the new tab/search (rev7 panel):
  // clear the data and show the honest "Loading..." until this shape's first response lands.
  useEffect(() => {
    const cached = cachedAnswer(cacheKey);
    if (cached) {
      setData(cached);
    } else if (!firstLoad.current) {
      setData(null);
      setLoading(true);
    }
  }, [cacheKey]);

  const load = useCallback(
    async (quiet: boolean): Promise<void> => {
      if (paused) return;
      const myReq = ++reqSeq.current;
      // "Loading..." only when there is nothing cached to show for this shape. A QUIET load (the 20 s
      // poll, a return to the tab, a refresh after a write) changes nothing on screen until its answer
      // lands (row 359 B): it used to turn the strip amber "Refreshing..." and back on every poll, which
      // read as the page flashing. The strip's "Queue live as of h:mm" moving on is the quiet signal.
      if (!quiet) setLoading(cachedAnswer(cacheKey) === undefined);
      // Row 866: the read is announced before it goes out, so the left column's count, looking at
      // this same shape, waits for it instead of asking the server a second time.
      const ticket = readStarted(cacheKey);
      try {
        const resp = await listEvents({
          direction, status, q: debouncedQ, source, notSource, excludeFaxFailures, groupBy,
        });
        // Cache BEFORE the supersede check: a response superseded by a tab switch is still the
        // freshest data for ITS OWN shape (otherwise a post-mutation reload that raced a
        // tab-follow leaves the origin tab's cache permanently pre-mutation - rev7 panel). The
        // tiny same-key out-of-order window this admits self-heals on the next 20s poll.
        readSucceeded(cacheKey, ticket, resp);
        if (myReq !== reqSeq.current) return; // superseded by a newer load; drop the UI update
        setData(resp);
        set('fresh', liveStamp());
      } catch {
        // The last good answer stays cached (the rows stay on screen), but the shape's newest outcome
        // is now "failed", so a reader that must not show a stale number shows none.
        readFailed(cacheKey, ticket);
        if (myReq !== reqSeq.current) return;
        setData((prev) => prev ?? { client, count: 0, items: [] });
        set('blocked', 'Queue unavailable');
      } finally {
        if (myReq === reqSeq.current) setLoading(false);
      }
    },
    [client, direction, status, debouncedQ, source, notSource, excludeFaxFailures, groupBy, paused, set, cacheKey],
  );

  useEffect(() => {
    const quiet = !firstLoad.current;
    firstLoad.current = false;
    void load(quiet);
  }, [load]);

  useEffect(() => {
    if (paused || !pollMs) return;
    const t = setInterval(() => void load(true), pollMs);
    return () => clearInterval(t);
  }, [load, pollMs, paused]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void load(true);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  return { data, loading, reload: () => load(true) };
}
